import { SELECTED_NETWORKS_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { getNetworkName } from '@/utils/network/getNetworkName'
import type { PopoverProps } from '@radix-ui/react-popover'
import { getNetworkImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { AccordionItem } from '../common/accordion'
import { RoundImage } from '../common/roundImage'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelect } from './filterSelect'
import { FilterListContent } from './filterSelectContent'

const getNetworkSearchTerms = (name: string) =>
  `${name.toLowerCase()} ${getNetworkName(name.toLowerCase()).toLowerCase()}`

interface Props {
  vaults: Vault[]
  className?: string
}

function useNetworkFilterData(vaults: Vault[]) {
  const { selectedNetworks, updateSearchState } = useVaultsSearchState()
  const networks = useMemo(() => {
    const unique = [...new Set(vaults.map((vault) => vault.network.name.toLowerCase()))]
    return unique.sort((a, b) => getNetworkName(a).localeCompare(getNetworkName(b)))
  }, [vaults])
  const { trackEvent } = useMixpanel()

  return {
    items: networks,
    selectedItems: selectedNetworks,
    setSelectedItems: (value: string[]) => {
      updateSearchState([SELECTED_NETWORKS_KEY, value])
      trackEvent('Filter used', { Filter: 'network', Value: value.join(', ') })
    },
    name: 'network',
    renderItemPrefix: (item: string) => (
      <RoundImage
        src={getNetworkImage(item)}
        fallback={getNetworkImage('fallback')}
        alt={`${item} network`}
        className="size-7"
      />
    ),
    searchNormalizer: getNetworkSearchTerms,
    itemFormatter: getNetworkName,
    capitalize: true,
  }
}

export function NetworkFilter({ vaults, className, modal }: Props & Pick<PopoverProps, 'modal'>) {
  const props = useNetworkFilterData(vaults)
  return <FilterSelect {...props} scrollClassName="max-h-56" modal={modal} className={className} />
}

export function NetworkFilterAccordion({ vaults }: Props) {
  const props = useNetworkFilterData(vaults)
  return (
    <AccordionItem value="network">
      <FilterAccordionTrigger name="network" />
      <FilterAccordionContent>
        <FilterListContent {...props} scrollClassName="max-h-48" />
      </FilterAccordionContent>
    </AccordionItem>
  )
}
